/* eslint-disable react/jsx-filename-extension */
import React from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Button from "../elements/Button";
import slugify from "../utils/slugify";

// Convert slug → readable title
function slugToTitle(slug) {
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Custom subtitles for each service page
const SERVICE_SUBTITLES = {
  "managed-it-services":
    "Fully managed IT support with monitoring, proactive maintenance, and predictable pricing.",
  "cybersecurity-services":
    "Protection from threats with advanced security monitoring and compliance tools.",
  "hardware-as-a-service":
    "Enterprise hardware with flexible monthly pricing and full lifecycle management.",
  "disaster-recovery":
    "Recover quickly from outages with strong backup systems and business continuity planning.",
  "cloud-solutions":
    "Cloud migration, optimisation and a modern infrastructure built for growth.",
  "it-consulting":
    "Strategic IT guidance to align your business goals and technology roadmap.",
  "vcio-services":
    "Fractional CIO leadership for budgeting, planning, and business-focused IT strategy.",
  "network-security":
    "Advanced protection for your network, data, and internal environments.",
  "hosted-private-cloud":
    "Secure and high-performance private cloud infrastructure for full control.",
  "managed-print-services":
    "Reduce print costs and improve workflow with managed print and device optimisation.",
  "robotic-process-automation":
    "Automate manual processes and increase efficiency with RPA solutions.",
};
const INDUSTRY_SUBTITLES = {
  "healthcare-and-medical-practices":
   "Secure and compliant IT solutions tailored for healthcare organizations.",
  "financial-services-and-accounting-firms":
   "Robust IT infrastructure and cybersecurity built for financial institutions.",
  "manufacturing-and-logistics": 
  "Smart automation, IoT integration, and reliable IT for production environments.",
  "retail-and-ecommerce":
   "Point-of-sale support, cloud systems, and secure networks for retail operations.",
  "education-and-training-institutions":
   "Reliable IT services for schools, institutions, online learning and student management.",
  "legal-firms":
   "Data security, compliance, and workflow optimization for law firms.",
  [slugify("Audiology")]:
    "Reliable and secure IT systems that support patient care, diagnostics, and clinic operations.",
  [slugify("Automotive")]:
    "Technology solutions that keep dealerships, service centers, and operations running smoothly.",
  [slugify("Aviation")]:
    "Mission-critical IT infrastructure built for safety, compliance, and operational efficiency.",
  "hospitality-and-tourism":
    "IT solutions that enhance guest experiences, streamline operations, and protect customer data.",
  "local-governments-and-municipalities":
    "Secure, scalable IT systems designed to support public services and community operations.",
  "nonprofit-organizations":
    "Cost-effective IT solutions that help nonprofits focus resources on impact, not infrastructure.",
  "professional-services-consulting-marketing-hr":
    "Flexible, secure IT environments that support collaboration, data protection, and growth.",
  "real-estate-and-property-management":
    "Technology solutions that simplify property operations, tenant management, and data security.",
};
const ABOUT_SUBTITLES = {
  "our-mission-values": "Discover the principles and values that guide our company.",
  "our-team": "Meet the passionate people behind our company's success.",
  "events-hub": "Learn how our company began and the journey that shaped who we are.",
  [slugify("FAQS")]: "Explore opportunities to join our team and grow your career.",
};

// Static hero data for top-level pages
const HERO_DATA = [
  {
    match: (p) => p === "/services",
    title: "Our IT Services",
    subtitle: "Comprehensive solutions that support your business growth.",
    buttonText: "Book  a Consultation",
    buttonLink: "/book-consultation",
    bg: "hero/hero-services.jpg",
  },
  {
    match: (p) => p === "/industries",
    title: "Industries We Support",
    subtitle: "Tailored IT solutions built for your industry.",
    buttonText: "Book  a Consultation",
    buttonLink: "/book-consultation",
    bg: "hero/hero-industries.jpg",
  },
  {
    match: (p) => p === "/about" || p === "/about/",
    title: "About Us",
    subtitle: "Learn more about our mission and team.",
    bg: "/hero/hero-about.jpg",
  },
];

export default function UniversalHero({
  title: propTitle,
  subtitle: propSubtitle,
  buttonText: propButtonText,
  buttonLink: propButtonLink,
  bg: propBg,
}) {
  const { pathname } = useLocation();

  if (pathname === "/") {
    return null;
  }

  let title = "";
  let subtitle = "";
  let buttonText = "";
  let buttonLink = "";
  let bg = "";

  // 1) Exact top-level matches
  const config = HERO_DATA.find((h) => h.match(pathname));

  if (config) {
    ({
      title,
      subtitle,
      buttonText,
      buttonLink,
      bg,
    } = config);
  } else if (/^\/services\/[^/]+$/.test(pathname)) {
    const slug = pathname.split("/").pop();

    title = slugToTitle(slug);
    subtitle = SERVICE_SUBTITLES[slug] 
    ?? "Detailed information about this service.";

    buttonText = "Book  a Consultation";
    buttonLink = "/book-consultation";
    bg = `/hero/services/${slug}.jpg`;
  } else if (/^\/industries\/[^/]+$/.test(pathname)) {
    const slug = pathname.split("/").pop();

    title = slugToTitle(slug);

    subtitle = propSubtitle 
    ?? INDUSTRY_SUBTITLES[slug] 
    ?? "Industry-specific IT solutions tailored to your needs.";

    buttonText = propButtonText ?? "Book  a Consultation";
    buttonLink = propButtonLink ?? "/book-consultation";
    bg = propBg ?? `/hero/industries/${slug}.jpg`;
  } else if (/^\/about\/[^/]+\/?$/.test(pathname)) {
    const slug = pathname.replace(/\/$/, "").split("/").pop();
    console.log("HERO SLUG:", slug);

    title = slugToTitle(slug);

    subtitle = propSubtitle 
    ?? ABOUT_SUBTITLES[slug] 
    ?? "Learn more about this section.";

    bg = propBg ?? `/hero/about/${slug}.jpg`;
  } else if (/^\/contact(\/[^/]+)?$/.test(pathname)) {
    const slug = pathname.split("/").pop();

    title = slug && slug !== "contact"
      ? slugToTitle(slug)
      : "Contact Us";

    subtitle = propSubtitle 
      ?? "Get in touch with our team to discuss how we can support your business.";

    buttonText = propButtonText ?? "Send a Message";
    buttonLink = propButtonLink ?? "/contact";

    bg = propBg ?? "/hero/contact.png";
  } else {
    const slug = pathname.replace("/", "") || "Page";
    title = slugToTitle(slug);
    subtitle = "";
    buttonText = "";
    buttonLink = "";
    bg = "/hero/hero-services.jpg";
  }
  
  // ————— OVERRIDES FROM PROPS —————
  title = propTitle ?? title;
  subtitle = propSubtitle ?? subtitle;
  buttonText = propButtonText ?? buttonText;
  buttonLink = propButtonLink ?? buttonLink;
  bg = propBg ?? bg;

  const bgStyle = {
    backgroundImage: `url(${bg})`,
    backgroundSize: "cover",
    backgroundPosition: "right center",
  };
  const isBookingPage = pathname === "/book-consultation";
  return (
    <section
      className={`relative w-full flex items-center ${
        isBookingPage ? "min-h-[420px] lg:min-h-[480px]" : "min-h-[600px] lg:min-h-[700px]"
      }`}
      style={bgStyle}
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-surface-900/85 via-surface-900/60 to-surface-900/30" />

      <div className="relative z-10 container mx-auto px-6 lg:px-20 py-24">
        <div className="max-w-3xl">
          <h1 className="text-white font-extrabold leading-tight text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {title}
          </h1>

          {subtitle && (
            <p className="text-white/80 text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}

          {buttonText && (
            <div className="mt-10">
              <Button
                href={buttonLink}
                type="link"
                className="btn-premium text-base px-8 py-4"
              >
                {buttonText}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[80px] lg:h-[120px]">
          <path d="M0,0 C400,150 800,0 1200,80 L1200,120 L0,120 Z" fill="#fafbfd" />
        </svg>
      </div>
    </section>
  );
}

// PropTypes (fixes ESLint warning)
UniversalHero.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  bg: PropTypes.string,
};

UniversalHero.defaultProps = {
  title: undefined,
  subtitle: undefined,
  buttonText: undefined,
  buttonLink: undefined,
  bg: undefined,
};
